/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import { useAuth  } from '../context/AuthContext';
import { Image, Upload, Download, Trash2, X, Plus, ZoomIn, ZoomOut, Search, Grid, List } from 'lucide-react';
import './MediaPage.css';

const formatBytes = (b) => {
  if (!b) return '0 B';
  const k=1024, s=['B','KB','MB','GB'];
  const i=Math.floor(Math.log(b)/Math.log(k));
  return `${parseFloat((b/Math.pow(k,i)).toFixed(1))} ${s[i]}`;
};

export default function MediaPage() {
  const { user } = useAuth();
  const toast    = useToast();

  const [images,    setImages]    = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [zoom,      setZoom]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [view,      setView]      = useState('grid'); // grid | list
  const [dragOver,  setDragOver]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(`nexa_media_${user?._id||user?.id}`);
      if (saved) setImages(JSON.parse(saved));
    } catch {}
  }, [user]);

  const save = (list) => {
    try { localStorage.setItem(`nexa_media_${user?._id||user?.id}`, JSON.stringify(list)); } catch {}
    setImages(list);
  };

  const handleUpload = useCallback((fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (!files.length) { toast.error('Please select image files only'); return; }
    setUploading(true);
    let done = 0;
    const newImages = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({
          id: `img_${Date.now()}_${Math.random()}`,
          name: file.name, size: file.size,
          dataUrl: e.target.result,
          width: 0, height: 0,
          uploadedAt: new Date().toISOString(),
        });
        done++;
        if (done === files.length) {
          setImages(prev => { const updated=[...newImages,...prev]; save(updated); return updated; });
          setUploading(false);
          toast.success(`✅ ${files.length} image${files.length>1?'s':''} uploaded!`);
        }
      };
      reader.readAsDataURL(file);
    });
  }, [user, toast]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); };

  const handleDelete = (id) => {
    const updated = images.filter(i => i.id!==id);
    save(updated);
    if (selected?.id===id) setSelected(null);
    toast.info('Image deleted');
  };

  const handleDownload = (img) => {
    const a = document.createElement('a'); a.href=img.dataUrl; a.download=img.name; a.click();
  };

  const navigate = (dir) => {
    if (!selected) return;
    const idx = filtered.findIndex(i => i.id===selected.id);
    const next = dir==='next' ? filtered[(idx+1)%filtered.length] : filtered[(idx-1+filtered.length)%filtered.length];
    setSelected(next); setZoom(1);
  };

  // Keyboard nav
  React.useEffect(() => {
    if (!selected) return;
    const handler = (e) => {
      if (e.key==='ArrowRight') navigate('next');
      if (e.key==='ArrowLeft')  navigate('prev');
      if (e.key==='Escape')     { setSelected(null); setZoom(1); }
      if (e.key==='+')          setZoom(z => Math.min(z+.25, 4));
      if (e.key==='-')          setZoom(z => Math.max(z-.25, .25));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selected, images]);

  const filtered = images.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));
  const totalSize = images.reduce((s,i) => s+(i.size||0), 0);

  return (
    <div className="media-page">
      {/* Header */}
      <div className="media-header">
        <div>
          <h1><Image size={22} style={{color:'var(--purple)'}}/> Media Gallery</h1>
          <p className="media-subtitle">{images.length} images • {formatBytes(totalSize)}</p>
        </div>
        <div className="media-header-right">
          <div className="docs-search">
            <Search size={15}/>
            <input placeholder="Search images..." value={search} onChange={e=>setSearch(e.target.value)}/>
            {search && <button className="btn-icon" style={{width:24,height:24}} onClick={()=>setSearch('')}><X size={13}/></button>}
          </div>
          <div className="media-view-toggle">
            <button className={view==='grid'?'active':''} onClick={()=>setView('grid')} title="Grid view"><Grid size={16}/></button>
            <button className={view==='list'?'active':''} onClick={()=>setView('list')} title="List view"><List size={16}/></button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>inputRef.current?.click()} disabled={uploading}>
            {uploading ? <span className="loader" style={{width:14,height:14,borderWidth:2}}/> : <><Plus size={15}/> Upload</>}
          </button>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className={`media-dropzone ${dragOver?'active':''}`}
        onDragOver={e=>{e.preventDefault();setDragOver(true)}}
        onDragLeave={()=>setDragOver(false)}
        onDrop={handleDrop}
        onClick={()=>inputRef.current?.click()}
      >
        <Upload size={20}/> <span>Drop images here or click to upload</span>
        <span className="media-dropzone-sub">JPG, PNG, GIF, WebP supported</span>
      </div>

      <input ref={inputRef} type="file" multiple accept="image/*" style={{display:'none'}}
        onChange={e=>handleUpload(e.target.files)}/>

      {/* Gallery */}
      {filtered.length===0 ? (
        <div className="empty-state" style={{flex:1}}>
          <Image size={48}/>
          <h3>{search?'No images found':'No images yet'}</h3>
          <p>{search?'Try a different search term':'Upload your first image to get started'}</p>
          {!search && <button className="btn btn-primary btn-sm" onClick={()=>inputRef.current?.click()}><Upload size={14}/> Upload Image</button>}
        </div>
      ) : view==='grid' ? (
        <div className="media-grid">
          {filtered.map((img,i) => (
            <div key={img.id} className="media-card" onClick={()=>setSelected(img)}
              style={{animationDelay:`${i*0.03}s`}}>
              <img src={img.dataUrl} alt={img.name} className="media-thumb" loading="lazy"/>
              <div className="media-card-overlay">
                <div className="media-card-actions-hover">
                  <button className="media-action-btn" onClick={e=>{e.stopPropagation();handleDownload(img)}} title="Download">
                    <Download size={16}/>
                  </button>
                  <button className="media-action-btn danger" onClick={e=>{e.stopPropagation();handleDelete(img.id)}} title="Delete">
                    <Trash2 size={16}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="media-list">
          {filtered.map(img => (
            <div key={img.id} className="media-list-item" onClick={()=>setSelected(img)}>
              <img src={img.dataUrl} alt={img.name} className="media-list-thumb"/>
              <div className="media-list-info">
                <p className="media-list-name">{img.name}</p>
                <p className="media-list-meta">{formatBytes(img.size)} • {new Date(img.uploadedAt).toLocaleDateString()}</p>
              </div>
              <div className="media-list-actions">
                <button className="btn-icon" onClick={e=>{e.stopPropagation();handleDownload(img)}}><Download size={15}/></button>
                <button className="btn-icon" onClick={e=>{e.stopPropagation();handleDelete(img.id)}}><Trash2 size={15}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {selected && (
        <div className="media-lightbox" onClick={()=>{setSelected(null);setZoom(1);}}>
          <div className="media-lightbox-header" onClick={e=>e.stopPropagation()}>
            <span className="media-lightbox-name">{selected.name}</span>
            <div style={{display:'flex',gap:8}}>
              <button className="media-lb-btn" onClick={()=>setZoom(z=>Math.max(z-.25,.25))} title="Zoom out"><ZoomOut size={16}/></button>
              <span style={{fontSize:'.8rem',color:'var(--text-400)',minWidth:40,textAlign:'center'}}>{Math.round(zoom*100)}%</span>
              <button className="media-lb-btn" onClick={()=>setZoom(z=>Math.min(z+.25,4))} title="Zoom in"><ZoomIn size={16}/></button>
              <button className="media-lb-btn" onClick={()=>handleDownload(selected)} title="Download"><Download size={16}/></button>
              <button className="media-lb-btn danger" onClick={()=>handleDelete(selected.id)} title="Delete"><Trash2 size={16}/></button>
              <button className="media-lb-btn" onClick={()=>{setSelected(null);setZoom(1);}} title="Close"><X size={18}/></button>
            </div>
          </div>
          <div className="media-lightbox-body" onClick={e=>e.stopPropagation()}>
            <button className="media-lb-nav left" onClick={()=>navigate('prev')}>‹</button>
            <div className="media-lightbox-img-wrap" style={{overflow:'auto'}}>
              <img src={selected.dataUrl} alt={selected.name}
                style={{transform:`scale(${zoom})`,transformOrigin:'center',transition:'transform .2s ease',maxWidth:'80vw',maxHeight:'75vh',objectFit:'contain',display:'block'}}/>
            </div>
            <button className="media-lb-nav right" onClick={()=>navigate('next')}>›</button>
          </div>
          <div className="media-lightbox-footer" onClick={e=>e.stopPropagation()}>
            <span style={{fontSize:'.78rem',color:'var(--text-500)'}}>{filtered.findIndex(i=>i.id===selected.id)+1} / {filtered.length}</span>
            <span style={{fontSize:'.78rem',color:'var(--text-500)'}}>{formatBytes(selected.size)}</span>
          </div>
        </div>
      )}
    </div>
  );
}